import {
  FormValues,
  type FormActions,
  type FormConfig,
  type FormResults,
} from '@wix/form-viewer';
import { FormViewerContext, FormViewerHandle } from '@wix/form-viewer/widget';
import { useSettings } from '@wix/tpa-settings/react';
import { withSlotsPlaceholders } from '@wix/widget-plugins-ooi';
import {
  useBi,
  useEnvironment,
  useErrorBoundary,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import {
  CartConflictErrorType,
  EmptyStateErrorType,
  FormError,
  GenericErrorType,
} from '../../../types/errors';
import { FormStatus } from '../../../types/form-state';
import { mapToArray } from '../../../utils';
import { getErrorByType, hasErrorOfType } from '../../../utils/errors/errors';
import {
  isNotOffered,
  isOfferedAsPricingPlanOnlyWithoutPlansConnected,
} from '../../../utils/payment';
import {
  FormState,
  ServiceData,
  SlotService,
} from '../../../utils/state/types';
import { isServiceExist } from '../../../utils/validations/Validations';
import { FormControllerActions } from '../Actions/actions';
import { EditorContextProvider } from '../Hooks/useEditorContext';
import { FormActionsProvider } from '../Hooks/useFormActions';
import { FormComponentContextProvider } from '../Hooks/useFormComponentContext';
import useValidateWixForm from '../Hooks/useValidateWixForm';
import { AddonsDetails } from './AddonsDetails';
import { AddonsNotification } from './AddonsNotification';
import AlreadyBookedModal from './AlreadyBookedModal';
import BackButton from './BackButton/BackButton';
import BookingDetails from './BookingDetails/BookingDetails';
import ButtonsContainer from './ButtonsContainer';
import { CartConflictMessage } from './CartConflictMessage/CartConflictMessage';
import CartErrorModal from './CartErrorModal';
import CartModal from './CartModal/CartModal';
import { CouponCheckbox } from './CouponCheckbox';
import { WidgetDataHooks } from './dataHooks';
import EmptyStatePage from './EmptyStatePage/EmptyStatePage';
import Header from './Header';
import { handleIntakeFormEmailValidation } from './IntakeFormSection/handleIntakeFormEmailValidation';
import IntakeFormSection from './IntakeFormSection/IntakeFormSection';
import NumberOfParticipantsDropdown from './NumberOfParticipantsDropdown/NumberOfParticipantsDropdown';
import { PaymentSelectionContainer } from './PaymentSelectionContainer/PaymentSelectionContainer';
import { PaymentSummary } from './PaymentSummary/PaymentSummary';
import { ServiceInCartNotification } from './ServiceInCartNotification';
import SmsNotification from './SmsNotification/SmsNotification';
import { UnavailableSlotMessage } from './UnavailableSlotMessage';
import { classes, st } from './Widget.st.css';
import WixForm from './WixForm';

const Dialog = loadable(() => import('./Dialog/Dialog'));
const Toast = loadable(() => import('./Toast/Toast'));
const PolicyDescription = loadable(
  () => import('./PolicyDescription/PolicyDescription'),
);

const showGlobalLoaderList = [
  FormStatus.PROCESSING_SUBMIT_ACTION,
  FormStatus.REINITIALIZING,
];

export type ControllerProps = {
  actions: FormControllerActions;
} & FormState &
  WidgetProps & {
    wfActions: FormActions;
    wfConfig: FormConfig;
    wfResults: FormResults;
  };

const Widget: FC<WidgetProps<ControllerProps>> = (widgetProps) => {
  const {
    intakeForms,
    serviceData,
    actions,
    isPricingPlanInstalled,
    formSelectedSlot,
    businessInfo,
    isCart,
    couponInfo,
    memberDetails,
    errors,
    editorContext,
    status,
    dialog,
    cartModal,
    isServiceInCart,
    isUpsellPluginInstalled,
    existingBookingDetails,
    wixForms,
    selectedNumberOfParticipants,
    isSMSAutomationsEnabled,
    isSlotUnavailable,
  } = widgetProps;
  const { isMobile, isSSR } = useEnvironment();
  const { error } = useErrorBoundary();
  const settings = useSettings();
  const bi = useBi();
  const dateRegionalSettingsLocale = businessInfo?.dateRegionalSettingsLocale!;
  const { validateWixForm } = useValidateWixForm();
  const wixFormRef = React.useRef<FormViewerHandle>(null);
  const [wixFormValues, setWixFormValues] = useState<FormValues>({});

  const handleWixFormChange = useCallback(
    (values: FormValues) => {
      setWixFormValues(values);
      handleIntakeFormEmailValidation(values, intakeForms, actions, bi);
    },
    [intakeForms, actions, bi],
  );

  useEffect(() => {
    if (
      status === FormStatus.INITIALIZING ||
      status === FormStatus.REINITIALIZING
    ) {
      actions.initializeWidget();
    }
  }, [status, actions]);

  useEffect(() => {
    setWixFormValues({ ...wixFormValues, ...wixForms.initialValues });
  }, [wixForms.initialValues]);

  const renderedEmptyState = renderEmptyStatePage({
    error,
    status,
    errors,
    serviceData,
  });

  if (renderedEmptyState) {
    return renderedEmptyState;
  }

  const toastError = getErrorByType({
    errors,
    errorType: GenericErrorType,
  });

  const cartConflictError = getErrorByType({
    errors,
    errorType: CartConflictErrorType,
  });

  const showGlobalLoader = () => showGlobalLoaderList.includes(status);

  return (
    <FormViewerContext
      host={widgetProps.host}
      wfActions={widgetProps.wfActions}
      wfConfig={widgetProps.wfConfig}
      wfResults={widgetProps.wfResults}
    >
      <EditorContextProvider value={editorContext} key="form-main-widget">
        <FormActionsProvider
          value={{
            ...actions,
            validateWixForm: async () =>
              validateWixForm({
                actions,
                serviceData,
                wixFormRef,
                intakeForms,
                wixFormValues,
              }),
          }}
        >
          <FormComponentContextProvider
            value={{
              wixFormValues,
              isCartConflictError: !!cartConflictError,
              isUpsellPluginInstalled,
              dateRegionalSettingsLocale,
            }}
          >
            <Suspense
              fallback={
                <RenderGlobalLoader
                  dataHook={WidgetDataHooks.SUSPENSE_GLOBAL_SPINNER}
                  showGlobalLoader
                />
              }
            >
              <div
                className={st(classes.root, {
                  isMobile,
                  isProcessing: showGlobalLoader(),
                })}
                data-hook={WidgetDataHooks.MAIN_CONTAINER}
              >
                <div className={classes.wrapper}>
                  <RenderGlobalLoader showGlobalLoader={showGlobalLoader()} />
                  {toastError ? <Toast toastError={toastError} /> : null}
                  <BackButton />
                  {isSlotUnavailable && <UnavailableSlotMessage />}
                  <Header />
                  {cartConflictError ? (
                    <CartConflictMessage />
                  ) : (
                    <ServiceInCartNotification
                      isServiceInCart={isServiceInCart}
                    />
                  )}
                  <div className={classes.body}>
                    <div className={classes.formWrapper}>
                      <WixForm
                        formId={serviceData.formId}
                        wixFormRef={wixFormRef}
                        wixForms={wixForms}
                        formValues={wixFormValues}
                        handleFormChange={handleWixFormChange}
                      />
                      <NumberOfParticipantsDropdown serviceData={serviceData} />
                      {!isSSR && isServiceExist(formSelectedSlot) && (
                        <PaymentSelectionContainer
                          serviceData={serviceData}
                          memberDetails={memberDetails}
                          numberOfParticipants={selectedNumberOfParticipants}
                          isPricingPlanInstalled={isPricingPlanInstalled}
                          status={status}
                          errors={errors}
                        />
                      )}
                      <IntakeFormSection
                        intakeForms={intakeForms}
                        errors={errors}
                        isSingleService={serviceData.isSingleService}
                      />
                      <AddonsDetails serviceData={serviceData} />
                    </div>
                    <div className={classes.sidebar}>
                      <div className={classes.floatingContainer}>
                        <BookingDetails
                          serviceData={serviceData}
                          formSelectedSlot={formSelectedSlot}
                          numberOfParticipants={selectedNumberOfParticipants}
                          isCart={isCart}
                        />
                        <CouponCheckbox
                          serviceData={serviceData}
                          couponInfo={couponInfo}
                          isServiceInCart={isServiceInCart}
                        />
                        {serviceData.isSingleService && (
                          <PaymentSummary
                            serviceData={serviceData}
                            numberOfParticipants={selectedNumberOfParticipants}
                            status={status}
                            isCart={isCart}
                          />
                        )}
                        <AddonsNotification serviceData={serviceData} />
                        <SmsNotification
                          isMSAutomationEnabled={isSMSAutomationsEnabled}
                        />
                        <PolicyDescription serviceData={serviceData} />
                        <ButtonsContainer
                          serviceData={serviceData}
                          isCart={isCart}
                          isServiceInCart={isServiceInCart}
                          errors={errors}
                          status={status}
                          selectedNumberOfParticipants={
                            selectedNumberOfParticipants
                          }
                          existingBookingDetails={existingBookingDetails}
                          memberDetails={memberDetails}
                          isSlotUnavailable={isSlotUnavailable}
                        />
                      </div>
                    </div>
                  </div>
                  {dialog ? <Dialog {...dialog.props} /> : null}
                  <AlreadyBookedModal
                    existingBookingDetails={existingBookingDetails}
                    memberDetails={memberDetails}
                    serviceData={serviceData}
                  />
                  <CartErrorModal serviceData={serviceData} errors={errors} />
                  {cartModal?.status ? (
                    <CartModal
                      cartModalStatus={cartModal?.status}
                      serviceData={serviceData}
                      formSelectedSlot={formSelectedSlot}
                      bookingIds={cartModal.bookingIds}
                      settings={settings}
                    />
                  ) : null}
                </div>
              </div>
            </Suspense>
          </FormComponentContextProvider>
        </FormActionsProvider>
      </EditorContextProvider>
    </FormViewerContext>
  );
};

const RenderGlobalLoader = ({
  showGlobalLoader,
  dataHook,
}: {
  showGlobalLoader: boolean;
  dataHook?: string;
}) => {
  if (!showGlobalLoader) {
    return null;
  }
  return (
    <>
      <div
        className={st(classes.blanket)}
        data-hook={dataHook ? dataHook : WidgetDataHooks.GLOBAL_SPINNER}
      />
      <EmptyStatePage isProcessing={showGlobalLoader} />
    </>
  );
};

const renderEmptyStatePage = ({
  status,
  errors,
  error,
  serviceData,
}: {
  status: FormStatus;
  errors: FormError[];
  error?: Error | null;
  serviceData?: ServiceData;
}) => {
  const processingStatuses = [
    FormStatus.INITIALIZING,
    FormStatus.PROCESSING_USER_DETAILS,
    FormStatus.SSR,
  ];
  const isProcessing = processingStatuses.includes(status);

  const shouldShowEmptyStatePage = () =>
    isProcessing ||
    hasErrorOfType({ errorType: EmptyStateErrorType, errors }) ||
    error;

  if (shouldShowEmptyStatePage()) {
    if (error) {
      console.error('Empty state page rendered', error, errors);
    }
    return <EmptyStatePage isProcessing={isProcessing} />;
  }

  const someServiceNotValid =
    !serviceData?.slotServices ||
    mapToArray<SlotService>(serviceData.slotServices).some(
      ({ service }) =>
        isOfferedAsPricingPlanOnlyWithoutPlansConnected(service?.payment) ||
        isNotOffered(service?.payment),
    );

  if (someServiceNotValid) {
    console.error(
      'Empty state page rendered: some service not valid',
      error,
      errors,
    );
    return (
      <EmptyStatePage
        isProcessing={isProcessing}
        title="app.empty-state-page.no-pp.title"
        subtitle="app.empty-state-page.no-pp.subtitle"
      />
    );
  }

  return null;
};

export default withSlotsPlaceholders(Widget);
